import type { JSX } from 'react';
import { PageLoader } from '@/components/common/PageLoader';
import { usePlanStore } from '@/stores';
import { isSubscriptionActive } from '@/lib/subscription';
import { canOpenSection, featureLabel } from '@/lib/premiumFeatures';
import { LockedFeature } from './LockedFeature';

interface FeatureGateProps {
  /** Nav id / route segment, as keyed in `lib/premiumFeatures`. */
  page: string;
  children: JSX.Element;
}

/**
 * Per-feature entitlement gate. Unlike the old app-wide SubscriptionGate, this
 * wraps only premium routes — the app shell and free pages render without it,
 * so a non-subscriber can browse. When the user lacks an active plan the real
 * page never mounts (its guarded API calls never fire); they see
 * [[LockedFeature]] with an upgrade CTA instead.
 *
 * Mirrors SubscriptionGate's two safety rules: never flash a lock before the
 * first subscription read resolves, and fail OPEN when that read errors (a
 * transient failure must not lock out a paying user — the BE still enforces
 * entitlement on the endpoint itself).
 */
export function FeatureGate({ page, children }: FeatureGateProps) {
  const { subscription, subscriptionError, hydrated } = usePlanStore();

  // Wait for the first read so we never flash a lock to an entitled user.
  if (!hydrated) {
    return <PageLoader fullScreen={false} />;
  }

  // Fail open on a genuine read failure — same rationale as SubscriptionGate.
  if (subscriptionError && !isSubscriptionActive(subscription)) {
    return children;
  }

  if (!canOpenSection(subscription, page)) {
    return <LockedFeature feature={featureLabel(page)} />;
  }

  return children;
}
